import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, ScrollView, Alert } from 'react-native';
import { useNavigate } from 'react-router-dom';
import WebScrollView from '../components/WebScrollView';
import { Gift, Copy, Mail, ArrowLeft, Check, TrendingUp, Users } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { shadows } from '../lib/styles';
import { REFERRAL_CONFIG, generateReferralUrl, formatCentsToUSDShort } from '../config/referral';

interface ReferralInvitation {
  id: string;
  invitation_code: string;
  recipient_email: string | null;
  recipient_name: string | null;
  status: string;
  created_at: string;
  claimed_at: string | null;
  claimed_by: string | null;
}

interface ReferralCredit {
  id: string;
  amount_cents: number;
  credit_type: string;
  status: string;
  created_at: string;
}

interface ReferralStats {
  invitationsRemaining: number;
  invitationsSent: number;
  invitationsClaimed: number;
  totalCreditsEarned: number;
}

const Referrals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ReferralStats>({
    invitationsRemaining: 0,
    invitationsSent: 0,
    invitationsClaimed: 0,
    totalCreditsEarned: 0,
  });
  const [invitations, setInvitations] = useState<ReferralInvitation[]>([]);
  const [credits, setCredits] = useState<ReferralCredit[]>([]);
  const [referralCode, setReferralCode] = useState<string>('');
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Fetch profile to get referral code and invitations remaining
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('referral_code, referral_invitations_remaining, referral_credit_balance_cents')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (profile?.referral_code) {
        setReferralCode(profile.referral_code);
      }

      // Fetch invitations (only claimed ones now)
      const { data: invitationsData, error: invitationsError } = await supabase
        .from('referral_invitations')
        .select('*')
        .eq('referrer_id', user.id)
        .eq('status', 'claimed')
        .order('created_at', { ascending: false });

      if (invitationsError) throw invitationsError;

      // Fetch credits
      const { data: creditsData, error: creditsError } = await supabase
        .from('referral_credits')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (creditsError) throw creditsError;

      const totalCredits = creditsData?.reduce((sum, credit) => sum + credit.amount_cents, 0) || 0;
      const claimedCount = invitationsData?.length || 0;

      setStats({
        invitationsRemaining: profile?.referral_invitations_remaining ?? 0,
        invitationsSent: claimedCount,
        invitationsClaimed: claimedCount,
        totalCreditsEarned: totalCredits,
      });

      setInvitations(invitationsData || []);
      setCredits(creditsData || []);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      Alert.alert('Error', 'Failed to load referral information');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    if (Platform.OS === 'web') {
      navigator.clipboard.writeText(text);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'claimed':
        return '#10b981';
      case 'pending':
        return '#f59e0b';
      case 'expired':
        return '#ef4444';
      case 'cancelled':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#7c3aed" />
      </View>
    );
  }

  return (
    <WebScrollView style={styles.container}>
      <View style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <Pressable onPress={() => navigate('/dashboard')} style={styles.backButton}>
            <ArrowLeft size={20} color="#6b7280" />
          </Pressable>
          <View style={styles.headerContent}>
            <View style={styles.headerIcon}>
              <Gift size={32} color="#7c3aed" />
            </View>
            <Text style={styles.title}>Share HoneyDone</Text>
            <Text style={styles.subtitle}>
              Invite friends to HoneyDone and earn {formatCentsToUSDShort(REFERRAL_CONFIG.REFERRER_CREDIT_CENTS)} in credits for each member who joins!
            </Text>
          </View>
        </View>

        {/* Stats Cards */}
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Gift size={20} color="#7c3aed" />
            <Text style={styles.statValue}>{stats.invitationsRemaining}</Text>
            <Text style={styles.statLabel}>Available to Claim</Text>
          </View>
          <View style={styles.statCard}>
            <Users size={20} color="#3b82f6" />
            <Text style={styles.statValue}>{stats.invitationsClaimed}</Text>
            <Text style={styles.statLabel}>People Joined</Text>
          </View>
          <View style={styles.statCard}>
            <TrendingUp size={20} color="#10b981" />
            <Text style={styles.statValue}>{formatCentsToUSDShort(stats.totalCreditsEarned)}</Text>
            <Text style={styles.statLabel}>Credits Earned</Text>
          </View>
        </View>

        {/* How It Works */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>How It Works</Text>
          <View style={styles.card}>
            <View style={styles.stepItem}>
              <View style={styles.stepNumber}>
                <Text style={styles.stepNumberText}>1</Text>
              </View>
              <Text style={styles.stepText}>Generate a referral link or send a direct invite</Text>
            </View>
            <View style={styles.stepItem}>
              <View style={styles.stepNumber}>
                <Text style={styles.stepNumberText}>2</Text>
              </View>
              <Text style={styles.stepText}>Share the link with friends and family</Text>
            </View>
            <View style={styles.stepItem}>
              <View style={styles.stepNumber}>
                <Text style={styles.stepNumberText}>3</Text>
              </View>
              <Text style={styles.stepText}>
                When they join, you earn {formatCentsToUSDShort(REFERRAL_CONFIG.REFERRER_CREDIT_CENTS)} and they get {formatCentsToUSDShort(REFERRAL_CONFIG.NEW_MEMBER_CREDIT_CENTS)}!
              </Text>
            </View>
          </View>
        </View>

        {/* Your Referral Link */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Your Referral Link</Text>
          <View style={styles.card}>
            <Text style={styles.tabDescription}>
              Share this link with friends. Each person who signs up using your link will use one of your available invitations.
            </Text>
            
            <View style={styles.generatedLink}>
              <Text style={styles.generatedLabel}>Your Personal Link:</Text>
              <View style={styles.linkContainer}>
                <Text style={styles.linkText} numberOfLines={1}>
                  {generateReferralUrl(referralCode)}
                </Text>
                <Pressable
                  style={styles.copyButton}
                  onPress={() => copyToClipboard(generateReferralUrl(referralCode))}
                >
                  {copiedCode ? (
                    <Check size={18} color="#10b981" />
                  ) : (
                    <Copy size={18} color="#6b7280" />
                  )}
                </Pressable>
              </View>
              <Text style={styles.codeLabel}>Code: {referralCode}</Text>
              <Text style={styles.shareHint}>
                Share on social media, email signature, or anywhere you'd like!
              </Text>
            </View>
          </View>
        </View>

        {/* Who Has Joined */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Who Has Joined</Text>
          <View style={styles.card}>
            {invitations.length === 0 ? (
              <Text style={styles.emptyText}>No one has joined yet. Share your link above!</Text>
            ) : (
              <View style={styles.table}>
                {invitations.map((invitation) => (
                  <View key={invitation.id} style={styles.tableRow}>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Joined</Text>
                      <Text style={styles.tableCellValue}>
                        {new Date(invitation.claimed_at!).toLocaleDateString()}
                      </Text>
                    </View>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Credit Earned</Text>
                      <Text style={styles.creditAmount}>
                        {formatCentsToUSDShort(REFERRAL_CONFIG.REFERRER_CREDIT_CENTS)}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

        {/* Credit History */}
        {credits.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Credit History</Text>
            <View style={styles.card}>
              <View style={styles.table}>
                {credits.map((credit) => (
                  <View key={credit.id} style={styles.tableRow}>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Amount</Text>
                      <Text style={styles.creditAmount}>{formatCentsToUSDShort(credit.amount_cents)}</Text>
                    </View>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Type</Text>
                      <Text style={styles.tableCellValue}>
                        {credit.credit_type === 'referrer_bonus' ? 'Referral Bonus' : 'Welcome Bonus'}
                      </Text>
                    </View>
                    <View style={styles.tableCell}>
                      <Text style={styles.tableCellLabel}>Date</Text>
                      <Text style={styles.tableCellValue}>
                        {new Date(credit.created_at).toLocaleDateString()}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}
      </View>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  content: {
    padding: 24,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 32,
  },
  backButton: {
    marginBottom: 16,
  },
  headerContent: {
    alignItems: 'center',
  },
  headerIcon: {
    marginBottom: 16,
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
    maxWidth: 600,
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 32,
    ...(Platform.OS === 'web' && {
      flexWrap: 'wrap',
    }),
  },
  statCard: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#f3f4f6',
    minWidth: 150,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#111827',
    marginTop: 12,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 14,
    color: '#6b7280',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
    gap: 12,
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#7c3aed',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumberText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  stepText: {
    flex: 1,
    fontSize: 15,
    color: '#374151',
    lineHeight: 22,
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    marginBottom: 24,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: '#7c3aed',
  },
  tabText: {
    fontSize: 15,
    color: '#6b7280',
    fontWeight: '500',
  },
  tabTextActive: {
    color: '#7c3aed',
  },
  tabContent: {
    gap: 16,
  },
  tabDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 8,
  },
  generateButton: {
    backgroundColor: '#7c3aed',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  generateButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  generatedLink: {
    marginTop: 16,
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
  },
  generatedLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  linkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#ffffff',
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  linkText: {
    flex: 1,
    fontSize: 14,
    color: '#7c3aed',
  },
  copyButton: {
    padding: 4,
  },
  codeLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 8,
  },
  shareHint: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 12,
    fontStyle: 'italic',
  },
  form: {
    gap: 16,
  },
  inputGroup: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    fontSize: 15,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  emptyText: {
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'center',
    paddingVertical: 32,
  },
  table: {
    gap: 12,
  },
  tableRow: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  tableCell: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 120 : undefined,
  },
  tableCellLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  tableCellValue: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  creditAmount: {
    fontSize: 16,
    fontWeight: '700',
    color: '#10b981',
  },
});

export default Referrals;
